import React from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

import { Grid, Card, CardContent, Typography, Stack, Tooltip, useMediaQuery } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';

import { COLOR_PRIMARY, COLOR_PRIMARY_DARK, DARK_GREY } from 'src/const';
import { formatCalendarDayName } from 'src/utils/functions';
import { InfoTooltip } from 'src/App/HOC/Tooltips';
import '../../Dashboards/Dashboard.scss';
import '../../Dashboards/Pricing/Styles/Calendar.scss';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    calendar: {
      width: '100%',
      background: '#ffffffbb',
      mixBlendMode: 'multiply',
      border: 0,
    },
    calendarTiles: {
      height: 80,
      position: 'relative',
      top: 0,
      left: 0,
      fontSize: '16px',
      fontWeignt: '700',
      padding: '5px 10px',
      border: '1px solid black',
    },
  })
);

const theme = createTheme({
  palette: {
    primary: {
      main: COLOR_PRIMARY,
    },
  },
});

const CalendarView = ({ rates, isMobileScreen, lastCalDate }) => {
  const classes = useStyles();
  const matches = useMediaQuery(theme.breakpoints.down(769));

  return (
    <>
      <Grid
        item
        xs={12}
        container
        direction="column"
        justifyContent="flex-start"
        alignItems="center"
        sx={{
          width: isMobileScreen ? '98%' : '100%',
        }}
        spacing={4}
      >
        <Grid
          item
          xs={12}
          sx={{
            width: '100%',
            filter: 'drop-shadow(0 0 5px rgba(0, 0, 0, 0.1))',
          }}
        >
          <Card sx={{ width: '100%', background: '#ffffffbb', padding: 0, boxShadow: 'none' }}>
            <CardContent sx={{ position: 'relative', background: 'transparent' }}>
              <div className="bg__green"></div>
              <div className="bg__blue"></div>
              <Calendar
                calendarType="US"
                className={classes.calendar}
                defaultView="month"
                formatShortWeekday={(_, date) => formatCalendarDayName(date, matches)}
                minDate={new Date()}
                maxDate={new Date(lastCalDate)}
                minDetail="year"
                next2Label={null}
                prev2Label={null}
                showNeighboringMonth={false}
                tileClassName={classes.calendarTiles}
                tileContent={({ activeStartDate, date, view }) => {
                  const offset = date.getTimezoneOffset();
                  let dateString = new Date(date.getTime() - offset * 60 * 1000)
                    .toISOString()
                    .split('T')[0];

                  if (
                    new Date(dateString) > new Date() &&
                    +new Date(dateString) <= +new Date(lastCalDate) &&
                    view === 'month'
                  ) {
                    return (
                      <>
                        <InfoTooltip
                          title={
                            <Card
                              sx={{
                                boxShadow: 'none',
                                filter: 'drop-shadow(0 0 10px rgba(0, 0, 0, 0.2))',
                              }}
                            >
                              <CardContent sx={{ background: '#fff', position: 'relative' }}>
                                <div
                                  style={{
                                    background: '#9EFF96',
                                    width: '150px',
                                    height: '150px',
                                    borderRadius: '50%',
                                    position: 'absolute',
                                    right: '-70px',
                                    top: 0,
                                    filter: 'blur(50px)',
                                  }}
                                />
                                <Grid container direction="column" alignItems="flex-start">
                                  <div style={{ fontWeight: 500, zIndex: 2, width: '100%' }}>
                                    <div style={{ marginBottom: '10px' }}>
                                      <GetBookingStatusColorCode
                                        status={rates[`${dateString}`].status}
                                        size={'medium'}
                                      />
                                    </div>
                                    <div className="calendar__tooltip__text">
                                      <Typography
                                        variant="body2"
                                        className="calendar__tooltip__left"
                                      >
                                        Current Rate:
                                      </Typography>
                                      <Typography
                                        variant="body2"
                                        className="calendar__tooltip__right"
                                      >
                                        ${rates[`${dateString}`].rate}
                                      </Typography>
                                    </div>

                                    {rates[`${dateString}`].prevRate && (
                                      <div className="calendar__tooltip__text">
                                        <Typography
                                          variant="body2"
                                          className="calendar__tooltip__left"
                                        >
                                          Last Rate:
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          className="calendar__tooltip__right"
                                        >
                                          ${rates[`${dateString}`].prevRate}
                                        </Typography>
                                      </div>
                                    )}
                                  </div>
                                </Grid>
                              </CardContent>
                            </Card>
                          }
                        >
                          <Grid
                            container
                            direction="column"
                            justifyContent="flex-end"
                            alignItems="flex-end"
                            sx={{ height: 60 }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: COLOR_PRIMARY_DARK,
                                fontWeight: 'bold',
                                fontSize: '16px',
                              }}
                            >
                              {rates[`${dateString}`].rate}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                textDecoration: 'line-through',
                                color: DARK_GREY,
                                fontWeight: 'bold',
                              }}
                            >
                              {rates[`${dateString}`].prevRate
                                ? rates[`${dateString}`].prevRate
                                : ''}
                            </Typography>
                          </Grid>
                        </InfoTooltip>
                        <Tooltip
                          title={
                            <GetBookingStatusColorCode
                              status={rates[`${dateString}`].status}
                              size="small"
                            />
                          }
                          arrow
                        >
                          <div className="calendar__index__color__dot">
                            <GetBookingStatusColorCode
                              text={false}
                              status={rates[`${dateString}`].status}
                              size="medium"
                            />
                          </div>
                        </Tooltip>
                      </>
                    );
                  } else {
                    return null;
                  }
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} className="calendar__demand__index__bar">
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={3}>
            <GetBookingStatusColorCode status="booked" />
            <GetBookingStatusColorCode status="recentlyBooked" />
            <GetBookingStatusColorCode status="available" />
            <GetBookingStatusColorCode status="inactive" />
          </Stack>
        </Grid>
      </Grid>
    </>
  );
};

const GetBookingStatusColorCode = ({
  status,
  text = true,
  size = 'normal',
}: {
  status: 'booked' | 'recentlyBooked' | 'available' | 'inactive';
  text?: boolean;
  size?: 'normal' | 'small' | 'medium';
}) => {
  const colorArray = [
    { color: '#86F8FF', message: 'Booked' },
    { color: '#F95738', message: 'Recently Booked' },
    { color: '#71FF64', message: 'Available' },
    { color: '#EFEFEF', message: 'Inactive' },
  ];

  //color and text of the color dots
  let statusIndex: number = 0;

  if (status === 'booked') {
    statusIndex = 0;
  } else if (status === 'recentlyBooked') {
    statusIndex = 1;
  } else if (status === 'available') {
    statusIndex = 2;
  } else if (status === 'inactive') {
    statusIndex = 3;
  }

  // size of the color dots
  let sizeValue: number = 20;

  if (size === 'small') {
    sizeValue = 10;
  } else if (size === 'medium') {
    sizeValue = 15;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <div
        style={{
          height: sizeValue,
          width: sizeValue,
          minHeight: sizeValue,
          minWidth: sizeValue,
          backgroundColor: colorArray[statusIndex].color,
          borderRadius: '50%',
        }}
      />
      {text && (
        <Typography variant="body2" component="p">
          {colorArray[statusIndex].message}
        </Typography>
      )}
    </Stack>
  );
};

export default CalendarView;
